import React, { useCallback } from 'react';
import tw from 'twin.macro';
import { Seance } from 'containers/CahierJournal/DayColumn/Seance';
import { CJEvent } from '@teetsh/app/src/types/event';
import _flatten from 'lodash-es/flatten';
import { ADD_EVENT } from '@teetsh/app/src/containers/constants';
import { useUpdateCJSeance } from 'containers/CahierJournal/queries/useUpdateCJSeance';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import swap from '@teetsh/app/src/utils/arrays/swap';
import { getGroupColumnTitle } from './getGroupColumnTitle';
import { DraggableAndDroppable } from './DraggableAndDroppable';

interface Props {
  listOfEvents: CJEvent[][];
  classrooms: Classroom[];
  classroomsOrg: ClassroomsOrg;
  classroom: Classroom;
  collapsed: boolean;
  isMobile?: boolean;
  dayString: string;
  highlightedEventIds?: string[];
  index: number;
}

function isEventFromStartToEnd(event: CJEvent, listOfEvents: CJEvent[][]) {
  const minStart = Math.min(
    ...listOfEvents.flatMap(g => g.map(e => e.start.getTime())),
  );
  const maxEnd = Math.max(
    ...listOfEvents.flatMap(g => g.map(e => e.end.getTime())),
  );

  return event.start.getTime() === minStart && event.end.getTime() === maxEnd;
}

export function Lines(props: Props) {
  const {
    isMobile,
    listOfEvents,
    classrooms,
    classroomsOrg,
    classroom,
    collapsed,
    dayString,
    highlightedEventIds = [],
  } = props;
  const updateEvent = useUpdateCJSeance();
  const { guestMode } = useUser();
  const onReorder = useCallback(
    (sourceIndex: number, direction: -1 | 1, eventsInRow: CJEvent[][]) => {
      const newEvents: CJEvent[][] = swap(
        eventsInRow,
        sourceIndex,
        direction,
      ) as CJEvent[][];

      const res = _flatten(
        newEvents.map((evts, index) =>
          evts
            .filter(e => e.type !== ADD_EVENT && e.type !== 'loading')
            .map(e => ({ ...e, order: index })),
        ),
      );
      Promise.all(res.map(evt => updateEvent({ event: evt })));
    },
    [JSON.stringify(listOfEvents)],
  );

  const handleRotateGroups = useCallback((event: CJEvent) => {
    if (!event.ateliers || event.ateliers.length <= 1) return;

    const newAteliers = event.ateliers.map((atelier, index) => {
      // Get the previous index, wrapping around to the end if we're at the start
      const prevIndex =
        (index - 1 + event.ateliers.length) % event.ateliers.length;
      return {
        ...atelier,
        groupPivots: event.ateliers[prevIndex].groupPivots,
      };
    });

    updateEvent({
      event: {
        ...event,
        ateliers: newAteliers,
      },
    });
  }, []);

  return (
    <div className="break-inside-avoid" tw="flex gap-2 mb-2">
      {listOfEvents.map((events, index) => {
        const columnTitle = getGroupColumnTitle(
          events,
          classrooms,
          classroomsOrg,
          listOfEvents.length,
        );

        return (
          <div
            key={index}
            css={[
              tw`w-full flex flex-col min-w-0`,
              columnTitle &&
                tw`p-2 border border-dashed border-neutral-moderate rounded-lg`,
            ]}
          >
            {columnTitle && (
              <div tw="mb-1 font-bold text-center">{columnTitle}</div>
            )}
            <div tw="flex-grow flex flex-col gap-y-2">
              {events.map(event => {
                const seanceId = `${dayString}_${classroom.id}_${event.id}`;

                const seance = (
                  <Seance
                    rowIndex={props.index}
                    key={seanceId}
                    onChange={ev => updateEvent({ event: ev })}
                    collapsed={collapsed}
                    classroomsOrg={classroomsOrg}
                    classrooms={classrooms}
                    onReorder={(direction: -1 | 1) =>
                      onReorder(
                        index,
                        direction,
                        listOfEvents.map(evts =>
                          evts.filter(e => e.type !== ADD_EVENT),
                        ),
                      )
                    }
                    onRotateGroups={() => handleRotateGroups(event)}
                    isFirstEventInRow={index === 0}
                    isLastEventInRow={index === listOfEvents.length - 1}
                    isHighlighted={highlightedEventIds.includes(
                      event.id.split('-')[0],
                    )}
                    event={event}
                  />
                );

                if (event.type === ADD_EVENT) {
                  return seance;
                }

                return (
                  <DraggableAndDroppable
                    key={seanceId}
                    seanceId={seanceId}
                    isDragDisabled={
                      event.type === 'loading' ||
                      isMobile ||
                      guestMode?.permission === 'read'
                    }
                    isFullHeight={
                      isEventFromStartToEnd(event, listOfEvents) // take whole height if the event is from the start to the end of the line (example : Sieste)
                    }
                  >
                    {seance}
                  </DraggableAndDroppable>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
